import React, {useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import "./FCSelect.scss";

export interface FCSelectOption {
    value: string | number;
    label: string;
}

type FCSelectProps = {
    options: FCSelectOption[];
    value: string | number;
    onChange: (value: string | number) => void;
    placeholder?: string;
    id?: string;
};

const FCSelect: React.FunctionComponent<FCSelectProps> = ({options, value, onChange, placeholder, id}) => {

    const [open, setOpen] = useState(false);
    const [listStyle, setListStyle] = useState<React.CSSProperties>({});
    const ref = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    const selected = options.find((o) => String(o.value) === String(value));

    // Calcule la position de la liste en coordonnées viewport pour le portal
    const updateListPosition = () => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            setListStyle({
                position: "fixed",
                top: rect.bottom,
                left: rect.left,
                width: rect.width,
                zIndex: 9999,
            });
        }
    };

    // Fermer si clic extérieur — exclure le trigger ET la liste (portal)
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                ref.current && !ref.current.contains(e.target as Node) &&
                listRef.current && !listRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Recalculer la position si scroll ou resize pendant l'ouverture
    useEffect(() => {
        if (!open) return;
        const handleScroll = () => updateListPosition();
        window.addEventListener("scroll", handleScroll, true);
        window.addEventListener("resize", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll, true);
            window.removeEventListener("resize", handleScroll);
        };
    }, [open]);

    const handleToggle = () => {
        if (!open) updateListPosition();
        setOpen((o) => !o);
    };

    const handleSelect = (val: string | number) => {
        onChange(val);
        setOpen(false);
    };

    const list = open && ReactDOM.createPortal(
        <div className="fcSelectList" style={listStyle} ref={listRef} data-fcselect-portal="true">
            {options.map((opt) => {
                const isActive = String(opt.value) === String(value);
                return (
                    <button
                        key={opt.value}
                        type="button"
                        className={`fcSelectOption${isActive ? " fcSelectOptionActive" : ""}`}
                        onClick={() => handleSelect(opt.value)}
                    >
                        <span>{opt.label}</span>
                        {isActive && (
                            <span className="material-symbols-outlined fcSelectCheck">check_circle</span>
                        )}
                    </button>
                );
            })}
        </div>,
        document.body
    );

    return (
        <div className={`fcSelect${open ? " fcSelectOpen" : ""}`} ref={ref} id={id}>
            {/* Trigger */}
            <button type="button" className="fcSelectTrigger" onClick={handleToggle}>
                <span className={`fcSelectTriggerLabel${!selected ? " fcSelectPlaceholder" : ""}`}>
                    {selected ? selected.label : (placeholder ?? "Sélectionner…")}
                </span>
                <span className="material-symbols-outlined fcSelectChevron">keyboard_arrow_up</span>
            </button>

            {list}
        </div>
    );
};

export default FCSelect;
